import { TileMapMetaProvider } from "./tile_map_meta_provider"
import { Vector2D, VectorLine } from "./ray_casting"
import { Bounds, Region, TileMapBase, TileSize } from "./models"
import { autotile } from "./room/autotiler"
import { blankMap } from "./util"

export interface PolymapMeta {
    vectorLines: Array<VectorLine>
    autotileMap: TileMapBase<number>
}
export const buildPolyMap = (tilemapMetaProvider: TileMapMetaProvider): PolymapMeta => {
    const { isOcclusionAt } = tilemapMetaProvider
    let polyMap: Array<VectorLine> = []

    polyMap[0] = polyMap[1] = polyMap[2] = polyMap[3] = { a: { x: 0, y: 0 }, b: { x: 0, y: 0 } }

    const occlusionMap = blankMap(tilemapMetaProvider.width(), tilemapMetaProvider.height())

    for (let x = 0; x < tilemapMetaProvider.width(); x++) {
        for (let y = 0; y < tilemapMetaProvider.height(); y++) {
            occlusionMap[y][x] = isOcclusionAt(x, y) ? 1 : 0
        }
    }
    const blockingAuto = autotile(occlusionMap)

    const evaluteAutotile = (x: number, y: number, autoId: number): Array<VectorLine> => {
        const polyMap: Array<VectorLine> = []
        const o: Vector2D = { x: x * TileSize, y: y * TileSize }
        const offset = TileSize / 2

        if (autoId === 18) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 8) {
            polyMap.push({
                a: { x: o.x, y: o.y - offset + TileSize },
                b: { x: o.x + TileSize, y: o.y - offset + TileSize },
            })
        }
        if (autoId === 14) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }
        if (autoId === 15) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + offset, y: o.y + offset },
            })
        }

        if (autoId === 6) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y - offset },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 3) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y - offset },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x - offset, y: o.y + offset },
            })
        }

        if (autoId === 5) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }
        if (autoId === 2) {
            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + offset, y: o.y + offset },
            })
        }

        if (autoId === 13) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }
        if (autoId === 1) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
        }

        if (autoId === 19) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y },
                b: { x: o.x + offset, y: o.y + TileSize },
            })
            polyMap.push({
                a: { x: o.x + offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }

        if (autoId === 21) {
            polyMap.push({
                a: { x: o.x + offset, y: o.y + TileSize },
                b: { x: o.x + offset, y: o.y + offset },
            })

            polyMap.push({
                a: { x: o.x - offset, y: o.y + offset },
                b: { x: o.x + TileSize, y: o.y + offset },
            })
        }

        return polyMap
    }

    for (let x = 0; x < tilemapMetaProvider.width(); x++) {
        for (let y = 0; y < tilemapMetaProvider.height(); y++) {
            const autoId = blockingAuto[y][x]
            if (autoId) {
                polyMap = polyMap.concat(evaluteAutotile(x, y, autoId))
            }
        }
    }

    return {
        vectorLines: polyMap,
        autotileMap: blockingAuto,
    }
}

export const extractRegions = (
    roomId: string,
    json: any,
    regionClass: string[] = ["REGION"],
): Record<string, Region[]> => {
    const { layers } = json
    const objectLayer = layers.find(layer => layer.name === "objects")
    if (!objectLayer) {
        return {}
    }
    return objectLayer.objects
        .filter(object => regionClass.includes(object.class))
        .reduce((acc, meta) => {
            return {
                ...acc,
                [meta.name]: [
                    ...(acc[meta.name] || []),
                    {
                        locationId: meta.name,
                        roomId,
                        x: Math.floor(meta.x),
                        y: Math.floor(meta.y),
                        bounds: new Bounds(
                            Math.floor(meta.x),
                            Math.floor(meta.y),
                            Math.floor(meta.x) + meta.width,
                            Math.floor(meta.y) + meta.height,
                        ),
                        context: (meta.properties || []).reduce(
                            (acc, next) => ({
                                ...acc,
                                [next.name]: next.value,
                            }),
                            {},
                        ),
                    },
                ],
            }
        }, {})
}

export const extractBiomeRegions = (
    roomId: string,
    json: any,
    regionClass: string = "REGION",
): Record<string, Region[]> => {
    const regions = Object.values(extractRegions(roomId, json, ["BIOMEREGION"])).flat()
    return regions.reduce((acc, next) => {
        return {
            ...acc,
            [next.context.biome]: [...(acc[next.context.biome] || []), next],
        }
    }, {})
}
